import { Dayjs } from "dayjs";

import {
	PaymentMethod,
	RecordInvoicePaymentRequest,
} from "../../../types/finance/FinanceTypes";
import { dollarsToCents } from "../../../utils/finance/money";

/**
 * The pure half of `RecordPaymentModal` — the form values as antd hands them over, and the one
 * function that turns them into a request body. Separate from the `.tsx` so it can be tested
 * without rendering antd (and without dragging `runtimeEnv`'s `import.meta` through ts-jest).
 */
export interface RecordPaymentFormValues {
	/** What the operator typed, in DOLLARS. Converted here and nowhere else. */
	amountDollars?: number | null;
	method?: PaymentMethod;
	reference?: string;
	receivedAt?: Dayjs | null;
}

/**
 * Form values -> `POST /invoices/:id/payments` body.
 *
 * Three things this deliberately does:
 *   - converts dollars to integer cents through `dollarsToCents`, which recovers the decimal the
 *     operator typed (`1.005 * 100` is `100.49999999999999` in IEEE-754, and a naive round of
 *     that is a cent short);
 *   - omits `reference` when it is blank rather than sending `""`. The server validates it with
 *     `@IsOptional() @IsNotEmpty()`, so an empty string is a 400 while an absent key is fine;
 *   - formats `receivedAt` as a plain `YYYY-MM-DD`. It is the date the money ARRIVED, not a
 *     timestamp, and sending an ISO datetime would drag the browser's timezone into a date that
 *     becomes the invoice's `paidDate`.
 */
export function buildRecordPaymentRequest(
	values: RecordPaymentFormValues,
): RecordInvoicePaymentRequest {
	const body: RecordInvoicePaymentRequest = {
		amountCents: dollarsToCents(values.amountDollars ?? 0),
		method: values.method as PaymentMethod,
	};

	const reference = (values.reference ?? "").trim();
	if (reference) {
		body.reference = reference;
	}

	if (values.receivedAt) {
		body.receivedAt = values.receivedAt.format("YYYY-MM-DD");
	}

	return body;
}
